import { useNavigate } from "react-router-dom";
import { Image as ImageIcon, Video, Wand2, Sparkles, Bot, Zap, UserCircle, Package, Megaphone, Mic, TrendingUp, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import {
  SegmentedControl,
  type SegmentedControlItem,
} from "@/components/flowly/infra/SegmentedControl/SegmentedControl";
import { StudioHeaderTitle } from "./StudioHeaderTitle";

type AppId = "imageGenerator" | "ads" | "director" | "profilePhoto" | "productCreative" | "framesToVideo" | "textToVideo" | "talkingAvatar" | "baseballTrend";

type GenerateTranslationKey =
  | "framesToVideo"
  | "textToVideo"
  | "talkingAvatar"
  | "imageGeneratorDesc"
  | "adsCreatorDesc"
  | "imageDirectorDesc"
  | "profilePhotoDesc"
  | "productCreativeDesc"
  | "framesToVideoDesc"
  | "textToVideoDesc"
  | "talkingAvatarDesc"
  | "baseballTrendDesc";

const imageApps: Array<{
  id: AppId;
  route: string;
  title: { he: string; en: string };
  descriptionKey: GenerateTranslationKey;
  Icon: typeof Sparkles;
}> = [
  {
    id: "imageGenerator",
    route: "/generate-image",
    title: { he: "יצירת תמונה", en: "Image Generator" },
    descriptionKey: "imageGeneratorDesc",
    Icon: Wand2,
  },
  {
    id: "director",
    route: "/image-director-chat",
    title: { he: "סוכן תמונות", en: "Image Director" },
    descriptionKey: "imageDirectorDesc",
    Icon: Bot,
  },
  {
    id: "profilePhoto",
    route: "/profile-photo",
    title: { he: "תמונות תדמית", en: "Profile Photo" },
    descriptionKey: "profilePhotoDesc",
    Icon: UserCircle,
  },
];

const marketingApps: Array<{
  id: AppId;
  route: string;
  title: { he: string; en: string };
  descriptionKey: GenerateTranslationKey;
  Icon: typeof Sparkles;
}> = [
  {
    id: "ads",
    route: "/generate-product-catalog",
    title: { he: "סטודיו מוצר", en: "Product Studio" },
    descriptionKey: "adsCreatorDesc",
    Icon: Sparkles,
  },
  {
    id: "productCreative",
    route: "/product-creative",
    title: { he: "קרייטיב למוצר", en: "Product Creative" },
    descriptionKey: "productCreativeDesc",
    Icon: Package,
  },
];

const trendsApps: Array<{
  id: AppId;
  route: string;
  title: { he: string; en: string };
  descriptionKey: GenerateTranslationKey;
  Icon: typeof Sparkles;
}> = [
  {
    id: "baseballTrend",
    route: "/baseball-trend",
    title: { he: "Baseball Trend", en: "Baseball Trend" },
    descriptionKey: "baseballTrendDesc",
    Icon: Trophy,
  },
];

const videoApps: Array<{
  id: AppId;
  route: string;
  titleKey: "framesToVideo" | "textToVideo" | "talkingAvatar";
  descriptionKey: GenerateTranslationKey;
  Icon: typeof Sparkles;
}> = [
  {
    id: "framesToVideo",
    route: "/frames-to-video",
    titleKey: "framesToVideo",
    descriptionKey: "framesToVideoDesc",
    Icon: Zap,
  },
  {
    id: "textToVideo",
    route: "/text-to-video",
    titleKey: "textToVideo",
    descriptionKey: "textToVideoDesc",
    Icon: Sparkles,
  },
  {
    id: "talkingAvatar",
    route: "/talking-avatar",
    titleKey: "talkingAvatar",
    descriptionKey: "talkingAvatarDesc",
    Icon: Mic,
  },
];

interface AppAccent {
  /** Inactive icon-chip backdrop. */
  bg: string;
  /** Inactive icon color. */
  text: string;
  /** Selected card background — soft tint on top of the card surface. */
  activeBg: string;
  /** Selected card border. */
  activeBorder: string;
  /** Halo ring around the selected card. */
  activeRing: string;
  /** Selected icon chip — solid accent fill, paired with a white icon. */
  activeIconBg: string;
  /** Colored drop-shadow so the selected card lifts off the page. */
  activeShadow: string;
}

const APP_ACCENTS: Record<AppId, AppAccent> = {
  imageGenerator: {
    bg: "bg-amber-100", text: "text-amber-600",
    activeBg: "bg-amber-50/80 dark:bg-amber-500/10",
    activeBorder: "border-amber-300 dark:border-amber-500/40",
    activeRing: "ring-amber-300/50 dark:ring-amber-500/30",
    activeIconBg: "bg-amber-500",
    activeShadow: "shadow-amber-500/20",
  },
  ads: {
    bg: "bg-violet-100", text: "text-violet-600",
    activeBg: "bg-violet-50/80 dark:bg-violet-500/10",
    activeBorder: "border-violet-300 dark:border-violet-500/40",
    activeRing: "ring-violet-300/50 dark:ring-violet-500/30",
    activeIconBg: "bg-violet-500",
    activeShadow: "shadow-violet-500/20",
  },
  director: {
    bg: "bg-cyan-100", text: "text-cyan-600",
    activeBg: "bg-cyan-50/80 dark:bg-cyan-500/10",
    activeBorder: "border-cyan-300 dark:border-cyan-500/40",
    activeRing: "ring-cyan-300/50 dark:ring-cyan-500/30",
    activeIconBg: "bg-cyan-500",
    activeShadow: "shadow-cyan-500/20",
  },
  profilePhoto: {
    bg: "bg-rose-100", text: "text-rose-600",
    activeBg: "bg-rose-50/80 dark:bg-rose-500/10",
    activeBorder: "border-rose-300 dark:border-rose-500/40",
    activeRing: "ring-rose-300/50 dark:ring-rose-500/30",
    activeIconBg: "bg-rose-500",
    activeShadow: "shadow-rose-500/20",
  },
  productCreative: {
    bg: "bg-orange-100", text: "text-orange-600",
    activeBg: "bg-orange-50/80 dark:bg-orange-500/10",
    activeBorder: "border-orange-300 dark:border-orange-500/40",
    activeRing: "ring-orange-300/50 dark:ring-orange-500/30",
    activeIconBg: "bg-orange-500",
    activeShadow: "shadow-orange-500/20",
  },
  framesToVideo: {
    bg: "bg-indigo-100", text: "text-indigo-600",
    activeBg: "bg-indigo-50/80 dark:bg-indigo-500/10",
    activeBorder: "border-indigo-300 dark:border-indigo-500/40",
    activeRing: "ring-indigo-300/50 dark:ring-indigo-500/30",
    activeIconBg: "bg-indigo-500",
    activeShadow: "shadow-indigo-500/20",
  },
  textToVideo: {
    bg: "bg-emerald-100", text: "text-emerald-600",
    activeBg: "bg-emerald-50/80 dark:bg-emerald-500/10",
    activeBorder: "border-emerald-300 dark:border-emerald-500/40",
    activeRing: "ring-emerald-300/50 dark:ring-emerald-500/30",
    activeIconBg: "bg-emerald-500",
    activeShadow: "shadow-emerald-500/20",
  },
  talkingAvatar: {
    bg: "bg-pink-100", text: "text-pink-600",
    activeBg: "bg-pink-50/80 dark:bg-pink-500/10",
    activeBorder: "border-pink-300 dark:border-pink-500/40",
    activeRing: "ring-pink-300/50 dark:ring-pink-500/30",
    activeIconBg: "bg-pink-500",
    activeShadow: "shadow-pink-500/20",
  },
  baseballTrend: {
    bg: "bg-lime-100", text: "text-lime-600",
    activeBg: "bg-lime-50/80 dark:bg-lime-500/10",
    activeBorder: "border-lime-300 dark:border-lime-500/40",
    activeRing: "ring-lime-300/50 dark:ring-lime-500/30",
    activeIconBg: "bg-lime-500",
    activeShadow: "shadow-lime-500/20",
  },
};

const tabs = [
  {
    id: "images",
    icon: ImageIcon,
    path: "/generate-image",
    labelKey: "imagesTab" as const,
  },
  {
    id: "marketing",
    icon: Megaphone,
    path: "/generate-product-catalog",
    labelKey: "marketingStudioTab" as const,
  },
  {
    id: "videos",
    icon: Video,
    path: "/frames-to-video",
    labelKey: "videosTab" as const,
  },
  {
    id: "trends",
    icon: TrendingUp,
    path: "/baseball-trend",
    labelKey: "trendsTab" as const,
  },
] as const;

const tabPaths: Record<string, string> = {
  images: "/generate-image",
  marketing: "/generate-product-catalog",
  videos: "/frames-to-video",
  trends: "/baseball-trend",
};

interface StudioHeaderProps {
  activeTab: "images" | "marketing" | "videos" | "trends";
  activeApp?: AppId;
  children?: React.ReactNode;
}

export function StudioHeader({
  activeTab,
  activeApp,
  children,
}: StudioHeaderProps) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const segmentedItems: SegmentedControlItem[] = tabs.map((tab) => ({
    id: tab.id,
    icon: tab.icon,
    label: t.generate[tab.labelKey],
  }));

  const isVideoApp =
    activeApp === "framesToVideo" || activeApp === "textToVideo" || activeApp === "talkingAvatar";
  const isMarketingApp = activeApp === "ads" || activeApp === "productCreative";
  const isTrendsApp = activeApp === "baseballTrend";
  const currentApps = isTrendsApp
    ? trendsApps
    : isVideoApp
      ? videoApps
      : isMarketingApp
        ? marketingApps
        : imageApps;

  const appRoutes: Record<string, string> = Object.fromEntries(
    currentApps.map((app) => [app.id, app.route]),
  );

  return (
    <>
      <div className="bg-gradient-to-b from-muted/40 to-muted/70 border-b border-border/50" data-tour="studio-tabs">
        <PageContainer width="wide" className="py-3 md:py-4">
          <StudioHeaderTitle />

          <SegmentedControl
            items={segmentedItems}
            activeId={activeTab}
            onSelect={(id) => navigate(tabPaths[id])}
          />

          {activeApp && (
            <>
              <div
                key={activeTab}
                className={cn(
                  "grid gap-1.5 mt-3 md:gap-3 md:mt-4",
                  // Marketing has 2 apps → stays 2-col. Trends currently has 1
                  // app → centered single card. Images + videos both have 3 apps
                  // → 3-col on desktop, 2-col on mobile so cards stay readable.
                  isTrendsApp
                    ? "grid-cols-1 max-w-md mx-auto"
                    : isMarketingApp
                      ? "grid-cols-2"
                      : "grid-cols-2 md:grid-cols-3"
                )}
              >
                {currentApps.map((app) => {
                  const Icon = app.Icon;
                  const isActive = app.id === activeApp;
                  const accent = APP_ACCENTS[app.id];
                  const title =
                    "titleKey" in app
                      ? t.generate[app.titleKey]
                      : app.title[language];
                  const description = t.generate[app.descriptionKey];

                  return (
                    <button
                      key={app.id}
                      onClick={isActive ? undefined : () => navigate(appRoutes[app.id])}
                      disabled={isActive}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "group relative flex items-center gap-2 p-2 rounded-xl border transition-all duration-200 text-start md:flex-col md:items-start md:gap-0 md:p-4 md:rounded-2xl",
                        isActive
                          ? cn(
                              accent.activeBg,
                              accent.activeBorder,
                              accent.activeRing,
                              accent.activeShadow,
                              "ring-2 shadow-lg cursor-default"
                            )
                          : "bg-card border-border hover:border-primary/30 hover:shadow-sm md:hover:-translate-y-0.5 cursor-pointer"
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center justify-center shrink-0 rounded-lg w-7 h-7 transition-colors md:w-10 md:h-10 md:rounded-xl md:mb-3",
                          isActive ? accent.activeIconBg : accent.bg
                        )}
                      >
                        <Icon
                          className={cn(
                            "w-3.5 h-3.5 md:w-5 md:h-5",
                            isActive ? "text-white" : accent.text
                          )}
                        />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 md:flex-none md:w-full">
                        <span
                          className={cn(
                            "text-[13px] md:text-base font-semibold leading-tight md:mb-1 text-foreground"
                          )}
                        >
                          {title}
                        </span>
                        <span
                          className={cn(
                            "text-[11px] md:text-sm leading-snug line-clamp-1 md:line-clamp-2",
                            isActive ? "text-foreground/70" : "text-muted-foreground"
                          )}
                        >
                          {description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {children && (
            <div className="flex items-center gap-4 mt-3">{children}</div>
          )}
        </PageContainer>
      </div>
    </>
  );
}
